import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'login' | 'register';
}

export const AuthModal = ({ isOpen, onClose, defaultTab = 'login' }: AuthModalProps) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState('');

  // Supabase-Verbindung testen, wenn Modal geöffnet wird
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
      const testConnection = async () => {
        try {
          const { data, error } = await supabase.from('profiles').select('count').limit(1);
          if (error) {
            console.warn('Supabase-Verbindungstest fehlgeschlagen:', error);
          } else {
            console.log('Supabase-Verbindung erfolgreich getestet');
          }
        } catch (err) {
          console.warn('Supabase-Verbindungstest-Fehler:', err);
        }
      };
      testConnection();
    }
  }, [isOpen, defaultTab]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !fullName.trim()) {
      setError('Bitte fülle alle Felder aus');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Registrierung versuchen
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName.trim(),
          }
        }
      });

      if (signUpError) {
        console.error('Registrierungsfehler:', signUpError);
        throw signUpError;
      }

      if (!signUpData.user) {
        throw new Error('Keine Benutzerdaten von der Registrierung erhalten');
      }

      // Profil und Subscription werden jetzt automatisch über Datenbank-Trigger erstellt
      setRegistrationSuccess(true);
      setRegistrationEmail(email);
      toast.success(
        'Registrierung erfolgreich! Bitte überprüfe dein E-Mail-Postfach.',
        { duration: 8000 }
      );
      
      // Modal offen lassen, damit Benutzer die Bestätigungsinfo sehen kann
    } catch (error: any) {
      console.error('Detaillierter Registrierungsfehler:', error);
      
      // Benutzerfreundliche Fehlermeldungen
      if (error.message.includes('User already registered') || error.message.includes('already been registered')) {
        setError('Diese E-Mail-Adresse ist bereits registriert.');
      } else if (error.message.includes('Password should be at least') || error.message.includes('Password')) {
        setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      } else if (error.message.includes('Invalid email') || error.message.includes('Email')) {
        setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      } else if (error.message.includes('signup is disabled')) {
        setError('Die Registrierung ist derzeit deaktiviert.');
      } else if (error.message.includes('Database error')) {
        setError('Datenbankfehler. Bitte versuche es später erneut.');
      } else {
        setError(`Ein Fehler ist aufgetreten: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Bitte fülle alle Felder aus');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      toast.success('Erfolgreich angemeldet!');
      onClose();
      // Navigation zum Dashboard
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Anmeldefehler:', error);
      setError('Ungültige Anmeldedaten');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);

    try {
      // Verwende die korrekte Production-URL
      const redirectUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `${window.location.origin}/dashboard`
        : 'https://kaufpause.at/dashboard';

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        }
      });

      if (error) throw error;
      
      // Redirect erfolgt automatisch
    } catch (error: any) {
      console.error('Google Anmeldefehler:', error);
      setError('Fehler bei der Anmeldung mit Google');
      setGoogleLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setError('Bitte gib deine E-Mail-Adresse ein');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const redirectUrl = `${window.location.origin}/reset-password`;
      console.log('Sending password reset email with redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl,
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast.success('E-Mail zum Zurücksetzen des Passworts wurde gesendet!');
    } catch (error: any) {
      console.error('Fehler beim Zurücksetzen des Passworts:', error);
      setError('Fehler beim Senden der E-Mail. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            Willkommen bei KaufPause
          </DialogTitle>
          <DialogDescription className="text-center">
            Melde dich an oder erstelle ein Konto
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Anmelden</TabsTrigger>
            <TabsTrigger value="register">Registrieren</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">E-Mail</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Passwort</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full"
                />
              </div>
              {error && (
                <div className="bg-red-50 p-3 rounded text-red-600 text-sm">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading || googleLoading}
              >
                {loading ? 'Wird angemeldet...' : 'Anmelden'}
              </Button>
              
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">oder</span>
                </div>
              </div>
              
              <Button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full bg-white hover:bg-gray-50 text-gray-900 border border-gray-300"
                disabled={loading || googleLoading}
              >
                <div className="flex items-center justify-center">
                  <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                    <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                      <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                      <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                      <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                      <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
                    </g>
                  </svg>
                  {googleLoading ? 'Wird angemeldet...' : 'Mit Google anmelden'}
                </div>
              </Button>
              
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setResetEmail(email);
                    setActiveTab('reset');
                  }}
                  className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                >
                  Passwort vergessen?
                </button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="register">
            {registrationSuccess ? (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Registrierung erfolgreich! 🎉</h3>
                <p className="text-gray-600">
                  Wir haben dir eine Bestätigungs-E-Mail an <strong>{registrationEmail}</strong> gesendet.
                </p>
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200 space-y-2">
                  <p className="text-emerald-800 text-sm font-medium">
                    Bitte klicke auf den Link in der E-Mail, um dein Konto zu aktivieren.
                  </p>
                  <p className="text-emerald-700 text-xs">
                    Die Zustellung der E-Mail kann einige Minuten dauern. Schau auch im Spam-Ordner nach.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">E-Mail</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Passwort</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>
                {error && (
                  <div className="bg-red-50 p-3 rounded text-red-600 text-sm">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading || googleLoading}
                >
                  {loading ? 'Wird registriert...' : 'Registrieren'}
                </Button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">oder</span>
                  </div>
                </div>
                
                <Button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="w-full bg-white hover:bg-gray-50 text-gray-900 border border-gray-300"
                  disabled={loading || googleLoading}
                >
                  <div className="flex items-center justify-center">
                    <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                      <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                        <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                        <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                        <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                        <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
                      </g>
                    </svg>
                    {googleLoading ? 'Wird registriert...' : 'Mit Google registrieren'}
                  </div>
                </Button>
              </form>
            )}
          </TabsContent>

          <TabsContent value="reset">
            {resetEmailSent ? (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">E-Mail gesendet! 📧</h3>
                <p className="text-gray-600">
                  Wir haben eine E-Mail an <strong>{resetEmail}</strong> gesendet.
                </p>
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200 space-y-2">
                  <p className="text-emerald-800 text-sm font-medium">
                    Bitte klicke auf den Link in der E-Mail, um dein Passwort zurückzusetzen.
                  </p>
                  <p className="text-emerald-700 text-xs">
                    Die Zustellung der E-Mail kann einige Minuten dauern. Schau auch im Spam-Ordner nach.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => setActiveTab('login')}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 mt-2"
                >
                  Zurück zur Anmeldung
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">E-Mail</label>
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>
                {error && (
                  <div className="bg-red-50 p-3 rounded text-red-600 text-sm">
                    {error}
                  </div>
                )}
                <Button
                  type="button"
                  onClick={handlePasswordReset}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                >
                  {loading ? 'Wird gesendet...' : 'Passwort zurücksetzen'}
                </Button>
                <div className="text-center mt-4">
                  <button
                    type="button"
                  onClick={() => setActiveTab('login')}
                    className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                >
                  Zurück zur Anmeldung
                  </button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
